import { ApiACLRole, ApiAuthUser, ApiClient, ApiProject, ApiStatus } from "@incendium/api";
import { Check, Delete, Edit } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { initializeClientsThunk } from "Actions";
import AccessLevel from "Components/AccessLevel/AccessLevel";
import SpacedList from "Components/UI/SpacedList";
import { cell2Icons } from "consts";
import useAllProjects from "features/project/hooks/useAllProjects";
import { deleteUser } from "features/users/services/userService";
import { isTenantManagedUser } from "features/users/utils";
import { formatEnumVal } from "Helpers/enumToText";
import { useConfirmation } from "Hooks/useConfirmation";
import { useUser } from "Hooks/useUser";
import produce from "immer";
import { CallbackOrVal } from "Interfaces";
import moment from "moment";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clientListSelector } from "Selectors/clientListSelector";

function resolveClientDisplayName(clientId: number | undefined, clients: ApiClient[]): string {
  if (clientId == null || clientId === 0) {
    return "";
  }
  const c = clients.find((cl) => cl.id === clientId);
  return c?.businessName?.trim() || `Client #${clientId}`;
}

function resolveProjectDisplayName(
  projectId: number | undefined,
  clientId: number | undefined,
  projects: ApiProject[]
): string {
  if (projectId == null || projectId === 0) {
    return "";
  }
  if (clientId != null && clientId !== 0) {
    const exact = projects.find((pr) => pr.id === projectId && pr.clientId === clientId);
    if (exact?.name?.trim()) {
      return exact.name.trim();
    }
  }
  const byId = projects.find((pr) => pr.id === projectId);
  return byId?.name?.trim() || `Project #${projectId}`;
}

interface IUsersListProps {
  onEdit: (user: ApiAuthUser) => void;
  onNew?: () => void;
  users: ApiAuthUser[];
  setUsers: (val: CallbackOrVal<ApiAuthUser[]>) => void;
  title?: string;
}

function UsersList({ onEdit, onNew, users, setUsers, title }: IUsersListProps) {
  const dispatch = useDispatch();
  const { list: clientList } = useSelector(clientListSelector);
  const { allProjects } = useAllProjects();
  const { hasPermission, user: loggedinUser } = useUser();
  const handleDelete = useConfirmation();

  useEffect(() => {
    dispatch(initializeClientsThunk());
  }, [dispatch]);

  const onDelete = async (user: ApiAuthUser) => {
    handleDelete({
      title: `Are you sure you want to delete this user`,
      body: `This action can not be undone`,
      callback: async () => {
        await deleteUser(user.id!);
        setUsers(
          produce(users, (draft) => {
            const idx = draft.findIndex((d) => d.id === user.id);
            if (idx >= 0) {
              draft.splice(idx, 1);
            }
          })
        );
        return "User Removed";
      },
    });
  };

  return (
    <SpacedList
      title={title}
      action={
        hasPermission(ApiACLRole.PROJECT_ADMIN) && onNew ? (
          <Button onClick={onNew}>Add New User</Button>
        ) : undefined
      }
    >
      <TableHead>
        <TableRow>
          <TableCell>Email</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Role(s)</TableCell>
          <TableCell>Active</TableCell>
          <TableCell>Validated</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {users.map((user) => {
          const permissions = user.permissions || [];
          return (
            <TableRow key={user.id}>
              <TableCell className="primary">{user.email}</TableCell>
              <TableCell>{user.name}</TableCell>
              <TableCell>
                <Stack>
                  {permissions.filter(p => p.role !== ApiACLRole.UNSPECIFIED).map((p, idx) => {
                    const clientLabel =
                      p.clientId !== 0
                        ? resolveClientDisplayName(p.clientId, clientList)
                        : "";
                    const projectLabel =
                      p.projectId !== 0
                        ? resolveProjectDisplayName(p.projectId, p.clientId, allProjects)
                        : "";
                    return (
                    <Typography variant="body2" key={`${p.role}-${p.clientId}-${p.projectId}-${idx}`}>
                      {hasPermission(ApiACLRole.SUPER_ADMIN) ? <>{p.environment} | </> : null}
                      {formatEnumVal(p.role || "")}{" "}
                      {clientLabel ? <>(Client : {clientLabel})</> : null}
                      {projectLabel ? <>(Project : {projectLabel})</> : null}
                    </Typography>
                    );
                  })}
                </Stack>
              </TableCell>
              <TableCell>
                {user.status === ApiStatus.ACTIVE && <Check />}
              </TableCell>
              <TableCell>
                {user.verifiedAt
                  ? moment(user.verifiedAt).format("lll")
                  : "-"}
              </TableCell>
              <TableCell
                align="right"
                color="primary"
                sx={{ width: cell2Icons }}
              >
                <AccessLevel role={ApiACLRole.PROJECT_ADMIN}>
                  <>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onEdit(user)}
                      disabled={user.id === loggedinUser.id} // Prevent editing self
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="secondary"
                      disabled={
                        user.id === loggedinUser.id ||
                        isTenantManagedUser(user)
                      }
                      onClick={() => onDelete(user)}
                    >
                      <Delete />
                    </IconButton>
                  </>
                </AccessLevel>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </SpacedList>
  );
}

export default UsersList;
